import _ from 'lodash';
import { BracketAlgorithm } from '../../../common/bracketCalc';
import { TCompRuleComparator } from '../../../common/eventCompRules';
import { retry, RetryOptions } from '../../../common/retry';
import { TEventVideoEntityType } from '../../../common/videoUtils';
import { TCharacterLoadoutOption } from '../../../types/characterTypes';
import {
  BracketFormat,
  BracketPosition,
  RemoveEventBracketTeamMode,
  SyncRosterType,
  TGetBracketTeamListFilters
} from '../../../types/eventBracketTypes';
import {
  EsportEventType,
  EventCategory,
  EventCollectionType,
  EventMode,
  EventType,
  ICreateEventCollectionPayload,
  IEventCollaboratorSummary,
  IEventContentSummary,
  IEventRegistrationStepSummary,
  IEventVideoSummary,
  IEventWinnerPayload,
  LiveEventMode,
  TAddEventVideoPayload,
  TEditEventVideoPayload,
  TLegacyEventParams,
  TSaveEventUserRegistrationParams,
  TUpdateEventVideosPayload
} from '../../../types/eventTypes';
import { TGetNewsParams } from '../../../types/newsTypes';
import { IReactionSummary, ReactionEntityType } from '../../../types/reactionTypes';
import { TStorageTagSummary, TUpdateStorageTagBody } from '../../../types/storageTypes';
import { TCallback } from '../../../types/utilityTypes';
import {
  TAddonSettingsLoadResponse,
  TAddonSettingsSaveResponse,
  TCharacterAddonSettingsSaveParams
} from '../../../types/addonSettingsTypes';
import frontendLog from '../../libs/frontendLog';
import { buildUrl } from '../../libs/utils';
import { networkService } from './networkService';

const callbackResolve = (callback: any) => (data: any) => callback(null, data);
const callbackReject = (callback: any) => (err: any) => callback(err);

export const PERFORM_GET = (url: string, callback: TCallback<any>): Promise<void> => {
  return networkService.get(url).then(callbackResolve(callback), callbackReject(callback));
};

export const PERFORM_DELETE = (url: string, params: any, callback: TCallback<any>): Promise<void> => {
  return networkService.delete(url, params).then(callbackResolve(callback), callbackReject(callback));
};

export const PERFORM_POST = (url: string, params: any, callback: TCallback<any>): Promise<void> => {
  return networkService.post(url, params).then(callbackResolve(callback), callbackReject(callback));
};

export const PERFORM_PUT = (url: string, params: any, callback: TCallback<any>): Promise<void> => {
  return networkService.put(url, params).then(callbackResolve(callback), callbackReject(callback));
};

export const PERFORM_PATCH = (url: string, params: any, callback: TCallback<any>): Promise<void> => {
  return networkService.patch(url, params).then(callbackResolve(callback), callbackReject(callback));
};

export type TGetCharacterLoadoutsParams = {
  region: string;
  realm: string;
  character: string;
};

export type TEventBodyPayload = {
  title: string;
  shortTitle: string | null;
  privacy: string;
  slug: string;
  type: string;
  esportType: EsportEventType;
  category: EventCategory;
  supportedRegions: string[];
  successfulDescription: string;
  successfulDescriptionHtml: string;
  checkinSuccessfulDescription: string;
  checkinSuccessfulDescriptionHtml: string;
  teamsCount: number | null;
  maxTeamSize: number | null;
  minTeamSize: number | null;
  webhookChannelId: string;
  webhookGuildId: string;
  description: string;
  prize: string;
  logoImagePath: string | null;
  logoImage2Path: string | null;
  headerImagePath: string | null;
  backgroundImagePath: string | null;
  logoImageId: number | null;
  logoImage2Id: number | null;
  headerImageId: number | null;
  backgroundImageId: number | null;
  statsImageId: number | null;
  registrationStartsDate: string | null;
  registrationEndsDate: string | null;
  eventStartsDate: string | null;
  eventEndsDate: string | null;
  publishedDate: string | null;
  sections: IEventContentSummary[];
  registrationSteps: IEventRegistrationStepSummary[];
  videos: IEventVideoSummary[];
  usesBBCode: boolean;
  eventTypeLabel: string;
  eventTypeLabelHtml: string;
};
export type TAddCollectionWinnerParams = {
  id: number;
  payload: IEventWinnerPayload;
};
export type TEditCollectionWinnerParams = {
  id: number;
  winnerTeamId: number;
  payload: IEventWinnerPayload;
};

export type TEditEventWinnerParams = {
  eventSlug: string;
  winnerTeamId: number;
  payload: IEventWinnerPayload;
};

export type TRemoveCollectionWinnerParams = {
  id: number;
  winnerTeamId: number;
};

export type TAddEventWinnerParams = {
  eventSlug: string;
  payload: IEventWinnerPayload;
};

export type TRemoveEventWinnerParams = {
  eventSlug: string;
  winnerTeamId: number;
};

export type TGetEventCollectionListParams = {
  search?: string;
  type?: EventCollectionType;
};

export type TGetEventCollectionDetailsParams = {
  id: number;
};

export type TDeleteEventCollectionParams = {
  id: number;
};

export type TGetEventCollectionSuggestionsQuery = {
  title?: string;
  limit?: number;
};

export type TGetEventListDataParams = {
  page: number;
  pageSize: number;
  type?: EventType | null;
  search?: string;
  mode?: EventMode | null;
};

export type TEditEventVideoParams = TEditEventVideoPayload;
export type TAddEventVideoParams = TAddEventVideoPayload;

export type TUpdateEventVideosParams = TUpdateEventVideosPayload;

export type TGetEventVideosParams = {
  entityId: number;
  entityType: TEventVideoEntityType;
};

export type TRemoveEventVideoParams = {
  id: number;
};

export type TUnlinkFinalEventVideo = {
  id: number;
  eventSlug: string | null;
};

export type TConvertBBCodeToHtml = {
  bbcode: string;
};

export type TViewEventUserRegistrationParams = {
  slug: string;
  inviteCode: string;
};

export type TViewUserEventRegistrationDetailsParams = {
  slug: string;
};

export type TCancelEventUserRegistrationParams = {
  slug: string;
};

export type TCancelEventEntriesTeamRegistrationParams = {
  slug: string;
  teamId: number;
};

export type TEventForceCheckIn = {
  eventSlug: string;
  teamId: number;
};
export type TGetEventTeamList = {
  eventSlug: string;
  page: number;
  pageSize: number;
};
export type TGetEventReminderListParams = {
  eventSlug: string;
};

export type TViewEventTeamDetailsParams = {
  team: string;
  namespace: string;
};

export type TgetEventManagementBracketListParams = {
  eventSlug: string;
};

export type TGetEventBracketDetailsParams = {
  eventSlug: string;
  bracketSlug: string;
};

export type TGetEventBracketGameRosterParams = {
  eventSlug: string;
  bracketSlug: string;
  matchId: number;
  gameId: number;
};

export type TAddEventBracketPlaceholders = {
  eventSlug: string;
  bracketSlug: string;
};

export type TAddEventBracketDungeonParams = {
  eventSlug: string;
  bracketSlug: string;
  payload: TEventBracketDungeonPayload;
};

export type TRemoveEventBracketDungeonParams = {
  eventSlug: string;
  bracketSlug: string;
  id: number;
};

export type TGetEventBracketTeamListParams = {
  eventSlug: string;
  bracketSlug: string;
  filters: TGetBracketTeamListFilters;
};

export type TGetEventBracketMatchesParams = {
  eventSlug: string;
  bracketSlug: string;
};

export type TGetEventBracketMatchDetailsParams = {
  eventSlug: string;
  bracketSlug: string;
  matchId: number;
};

export type TGetEventBracketMatchGamesParams = {
  eventSlug: string;
  bracketSlug: string;
  matchId: number;
};

export type TGetEventBracketMatchGameDetailsParams = {
  eventSlug: string;
  bracketSlug: string;
  matchId: number;
  gameId: number;
};

export type TCreateEventBracketParams = {
  eventSlug: string;
  payload: TEventBracketPayload;
};

export type TEditEventBracketParams = {
  eventSlug: string;
  bracketSlug: string;
  payload: TEventBracketPayload;
};

export type TEditEventBracketMatchDetailsParams = {
  eventSlug: string;
  bracketSlug: string;
  matchId: number;
  payload: TEventBracketMatchPayload;
};

export type TEditEventBracketMatchGameDetailsParams = {
  eventSlug: string;
  bracketSlug: string;
  matchId: number;
  gameId: number;
  payload: TEventBracketMatchGamePayload;
};

export type TSaveSelfReportScoresPayload = {
  teamId: number;
  matchId: number;
  results: Record<number, number | null>;
};

export type TSaveSelfReportScoresParams = {
  eventSlug: string;
  bracketSlug: string;
  matchId: number;
  gameId: number;
  payload: TSaveSelfReportScoresPayload;
};

export type TRemoveEventBracketParams = {
  eventSlug: string;
  bracketSlug: string;
};

export type TCopyEventBracketParams = {
  eventSlug: string;
  bracketSlug: string;
};

export type TAddEventBracketTeam = {
  eventSlug: string;
  bracketSlug: string;
  team: any;
};

export type TRemoveEventBracketTeam = {
  eventSlug: string;
  bracketSlug: string;
  team: any;
};

export type TRemoveEventBracketTeams = {
  eventSlug: string;
  bracketSlug: string;
  mode: RemoveEventBracketTeamMode;
};

export type TCheckInEventBracketTeam = {
  eventSlug: string;
  bracketSlug: string;
  bracketTeamId: number;
};

export type TCheckOutEventBracketTeam = {
  eventSlug: string;
  bracketSlug: string;
  bracketTeamId: number;
};

export type TCreateBracketMatches = {
  eventSlug: string;
  bracketSlug: string;
};

export type TAddEligibleTeamsToBracket = {
  eventSlug: string;
  bracketSlug: string;
};

export type TDeleteBracketMatches = {
  eventSlug: string;
  bracketSlug: string;
};

export type TResetBracketMatches = {
  eventSlug: string;
  bracketSlug: string;
};

export type TSeedBracketMatches = {
  eventSlug: string;
  bracketSlug: string;
  algorithm: BracketAlgorithm;
};

export type TSeedBracketTeamsRandomly = {
  eventSlug: string;
  bracketSlug: string;
};

export type TUpdateBracketTeamPayload = {
  teamId: number;
  bracketTeamName: string;
  points: number | null;
  region: string;
  subRegion?: string | null;
  bracketLogoId?: number | null;
  noteIcon?: string | null;
  note?: string | null;
};

export type TUpdateBracketTeam = {
  bracketSlug: string;
  eventSlug: string;
  payload: TUpdateBracketTeamPayload;
};

export type TBatchUpdateBracketMatchesPayload = {
  backgroundColor?: string;
  borderColor?: string;
  videoType?: string;
  videoId?: string;
  placeholdersUpdate?: boolean;
  round: number;
  position: BracketPosition;
  rosterType: SyncRosterType;
};

export type TBatchUpdateBracketMatches = {
  eventSlug: string;
  bracketSlug: string;
  payload: TBatchUpdateBracketMatchesPayload;
};

export type TBatchUpdateBracketTeams = {
  eventSlug: string;
  bracketSlug: string;
  payload: TBatchUpdateBracketTeamsPayload;
};

export type TBatchUpdateBracketTeamsPayload = {
  region?: string;
  subRegion?: string;
  points?: number;
};

export type TImportCSVEventBracketTeamsParams = {
  eventSlug: string;
  bracketSlug: string;
  formData: FormData;
};

export type TGetEventBracketTeamSuggestionsParams = {
  eventSlug: string;
  bracketSlug: string;
  name: string;
  limit?: number;
};

export type TGetCollectionWinnerSuggestionsParams = {
  collectionId: number;
  name: string;
  limit?: number;
};

export type TGetEventWinnerSuggestionsParams = {
  eventSlug: string;
  name: string;
  limit?: number;
};

export type TViewEventListParams = {
  mode: EventMode;
  category: EventCategory;
};
export type TEventDetailsParams = {
  eventSlug: string;
};

export type TEventBracketPayload = {
  id?: number;
  title: string;
  slug: string;
  visibility: string;
  format: BracketFormat;
  rounds?: number | null;
  startAtRoundCount?: number | null;
  stopAtRoundCount?: number | null;
  bracketTooltip?: string | null;
  showSeeds: boolean;
  deathPenaltySeconds?: number | null;
  backgroundImagePath?: string | null;
  backgroundImageId?: number | null;
  timelineBracketStartIcon?: string | null;
  timelineBracketEndIcon?: string | null;
  teamsCount: number | null;
  thirdPlaceWinner?: boolean;
  numTieBreakerMatches?: number;
  requireTeamCheckin?: boolean;
  autoSelfScoreEnabled?: boolean;
  supportsPoints?: boolean;

  rulesetSeasonSlug?: string | null;
  rulesetType?: string | null;
  rulesetRealmType?: string;
  rulesetRestrictsSpecs?: boolean;
  rulesetRestrictsReplays?: boolean;
  rulesetScoringAlgorithm?: string | null;
  rulesetTimedRunsOnly?: boolean;
  rulesetLiveTrackedOnly?: boolean;
  rulesetMinRequiredRuns?: number;
  rulesetQualifyByStartTime?: boolean;
  rulesetMaxScoredDungeons?: number | null;
  rulesetMinKeystoneLevel?: number | string | null;
  rulesetMaxKeystoneLevel?: number | string | null;
  requiresRegistration?: boolean;

  publishedDate?: string | null;
  gracePeriod: number;
  bracketStartsDate?: string | null;
  bracketEndsDate?: string | null;
  usStartsAt?: string | null;
  usEndsAt?: string | null;
  euStartsAt?: string | null;
  euEndsAt?: string | null;
  twStartsAt?: string | null;
  twEndsAt?: string | null;
  krStartsAt?: string | null;
  krEndsAt?: string | null;
  cnStartsAt?: string | null;
  cnEndsAt?: string | null;
  checkinStartsDate?: string | null;
  checkinEndsDate?: string | null;

  allowedUserIds?: string;
  disallowedUserIds?: string;
};

export type TEventSpecRestrictionPayload = {
  specIds?: number[];
};

export type TAddEventSpecRestrictionParams = {
  eventSlug: string;
  bracketSlug: string;
  payload: TEventSpecRestrictionPayload;
};

export type TDeleteEventSpecRestrictionParams = {
  eventSlug: string;
  bracketSlug: string;
  specIds: number[];
};

export type TEventBracketCompRulePayload = {
  comparator: TCompRuleComparator;
  playerCount: number;
  specIds: number[];
};

export type TSetEventBracketCompRulesParams = {
  eventSlug: string;
  bracketSlug: string;
  rules: TEventBracketCompRulePayload[];
};

export type TEventBracketDungeonPayload = {
  id?: number;
  zoneId: number;
  visibility?: boolean;
  mythicLevel?: number;
  affix_0_id?: number;
  affix_1_id?: number;
  affix_2_id?: number;
  affix_3_id?: number;
};

export type TEventBracketMatchPayload = {
  firstTeamId: number;
  secondTeamId: number;
  forcedWinnerTeamId: number;
  firstTeamStatus: string;
  secondTeamStatus: string;
  firstTeamPlaceholder: string | null;
  secondTeamPlaceholder: string | null;
  gamesCount: number;
  status: string;
  backgroundColor: string | null;
  borderColor: string | null;
};

export type TEventBracketMatchGamePayload = {
  name: string;
  value: any;
  type: string | null;
};

export type TGetEventCollaboratorSuggestionsParams = {
  slug: string;
  name: string;
  limit?: number;
};

export type TAddEventCollaborator = {
  slug: string;
  collaborator: IEventCollaboratorSummary;
};

export type TRemoveEventCollaborator = {
  slug: string;
  collaborator: IEventCollaboratorSummary;
};

export type TGetEventCollectionSuggestionsParams = {
  collectionId: string;
  query?: TGetEventCollectionSuggestionsQuery;
};

export type TAddEventToEventCollectionParams = {
  eventSlug: string;
  collectionId: string;
};

export type TRemoveEventFromEventCollectionParams = {
  eventSlug: string;
  collectionId: string;
};

export type TCreateEventCollectionParams = {
  eventSlug?: string | null;
} & ICreateEventCollectionPayload;

export type TEditEventCollectionParams = {
  id: number;
  payload: ICreateEventCollectionPayload;
};

export type TEventReplayRestrictionPayload = {
  replayId: number;
  seasonSlug: string;
};

export type TAddEventReplayRestrictionParams = {
  eventSlug: string;
  bracketSlug: string;
  payload: TEventReplayRestrictionPayload;
};

export type TDeleteEventReplayRestrictionParams = {
  eventSlug: string;
  bracketSlug: string;
  restrictionId: number;
};

export type TUpdateStorageItem = {
  id: number;
  tags?: string[];
  fileName?: string;
};

export type TGetTeamDetailsParams = {
  region: string;
  team: string;
  namespace?: string;
};

export type TGetHubCollectionParams = {
  expansionSlug: string;
  esportTypes?: string;
};

export type TGetUpcomingEventsParams = {
  esportTypes?: string;
};

export type TGetActiveEventsParams = {
  esportTypes?: string;
};

export type TGetEventHistoryParams = {
  esportTypes?: string;
};

export type TGetLiveEventParams = {
  mode?: LiveEventMode;
};

export type TGetLatestEventsParams = {
  esportTypes?: string;
};

export type TGetHubNewsParams = {
  tag?: string;
  limit?: number;
};

export type TCreateThreadPost = {
  threadId: number;
  content: string;
};
export type TGetKeystoneTeamRankingsParams = {
  eventId: number;
  season: string;
  region: string;
  page: number;
};
export type TGetKeystoneRunRankingsParams = {
  season: string;
  dungeon: string;
  region: string;
  page: number;
  strict: boolean;
  affixes?: string; // dash-separated affix names
  limit: number;
  minMythicLevel: number;
  maxMythicLevel: number;
  eventId: number;
  faction?: string;
  realm?: string;
  period?: number;
  recent?: boolean;
  search?: string;
};

export type TSearchAdvancedParams = {
  term: string;
  type?: 'all' | 'guild' | 'realm' | 'character';
};

export type TSearchParams = {
  term: string;
  type?: 'all' | 'guild' | 'team' | 'active_team' | 'realm' | 'character';
};

export const commonApiService = {
  /**
   * search
   */
  search(params: TSearchParams, callback: TCallback<any>) {
    const hostPath = '/api/search';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * saveGroupPrivacySettings
   */
  saveGroupPrivacySettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/privacy';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'saveGroupPrivacyCustomizationSettings request error');
      callback(err);
    });
  },

  /**
   * getGroupPrivacyCustomization
   */
  getGroupPrivacySettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/privacy';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback).catch((err) => {
      frontendLog.error({ err }, 'getGroupPrivacySettings request error');
      callback(err);
    });
  },

  listOpenGroupCharters(callback: TCallback<any>) {
    const hostPath = '/api/charters';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback).catch((err) => {
      frontendLog.error({ err }, 'listOpenGroupCharters request error');
      callback(err);
    });
  },

  getGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/details';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback).catch((err) => {
      frontendLog.error({ err }, 'getGroupCharter request error');
      callback(err);
    });
  },

  addCharacterToGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/add-member';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'addCharacterToGroupCharter request error');
      callback(err);
    });
  },

  createGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/create-charter';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'createGroupCharter request error');
      callback(err);
    });
  },

  renameGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/rename-charter';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'renameGroupCharter request error');
      callback(err);
    });
  },

  flagCharterForRename(params: any, callback: TCallback<any>) {
    const hostPath = '/admin/api/charters/flag-rename';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'flagCharterForRename request error');
      callback(err);
    });
  },

  createTeamFromGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/create-team';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'createTeamFromGroupCharter request error');
      callback(err);
    });
  },

  createGroupFromGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/groups';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'createGroupFromGroupCharter request error');
      callback(err);
    });
  },

  removeCharacterFromGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/remove-member';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'removeCharacterFromGroupCharter request error');
      callback(err);
    });
  },

  leaveGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/leave-charter';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'leaveGroupCharter request error');
      callback(err);
    });
  },

  deleteGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/delete-charter';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteGroupCharter request error');
      callback(err);
    });
  },

  viewGroupCharterInvite(code: string, callback: TCallback<any>) {
    const hostPath = '/api/charters/view-invite';
    const url = buildUrl(hostPath, { code });
    PERFORM_GET(url, callback).catch((err) => {
      frontendLog.error({ err }, 'viewGroupCharterInvite request error');
      callback(err);
    });
  },

  acceptGroupCharterInvite(code: string, callback: TCallback<any>) {
    const hostPath = '/api/charters/accept-invite';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, { code } as any)
      .then(callbackResolve(callback))
      .catch((err) => {
        frontendLog.error({ err }, 'viewGroupCharterInvite request error');
        callback(err);
      });
  },

  regenerateGroupCharterInvite(params: { charterId: number; eventId?: number }, callback: TCallback<any>) {
    const hostPath = '/api/charters/regenerate-invite';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, { id: params.charterId, eventId: params.eventId }, callback).catch((err) => {
      frontendLog.error({ err }, 'regenerateGroupCharterInvite request error');
      callback(err);
    });
  },

  deleteGroupCharterInvite(id: number, callback: TCallback<any>) {
    const hostPath = '/api/charters/delete-invite';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, { id }, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteGroupCharterInvite request error');
      callback(err);
    });
  },

  removeMemberFromGroupCharter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/remove-user';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'removeCharacterFromGroupCharter request error');
      callback(err);
    });
  },

  transferGroupCharterOwner(params: any, callback: TCallback<any>) {
    const hostPath = '/api/charters/transfer-owner';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'transferGroupCharterOwner request error');
      callback(err);
    });
  },

  /**
   * getGeneratedTeamNames
   */
  getGeneratedTeamNames(params: any, callback: TCallback<any>) {
    const hostPath = '/api/teams/name-generator';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * saveCharacterCustomizationSettings
   */
  saveCharacterCustomizationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/profile';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'saveCharacterCustomizationSettings request error');
      callback(err);
    });
  },

  /**
   * unclaimUserCharacter
   */
  unclaimUserCharacter(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/unclaim';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * getGroupCustomizationSettings
   */
  getGroupCustomizationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/customizations';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * saveGroupCustomizationSettings
   */
  saveGroupCustomizationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/groups/customizations';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'saveGroupCustomizationSettings request error');
      callback(err);
    });
  },

  /**
   * getCharacterCustomizationSettings
   */
  getCharacterCustomizationSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/profile';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * saveCharacterPrivacySettings
   */
  saveCharacterPrivacySettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/characters/privacy';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'saveCharacterPrivacySettings request error');
      callback(err);
    });
  },

  /**
   * getUserCharacterAddonSettings
   */
  getUserCharacterAddonSettings(params: { character_id: number }, callback: TCallback<TAddonSettingsLoadResponse>) {
    const hostPath = '/api/settings/characters/addon';
    const url = buildUrl(hostPath, params);
    networkService.get(url).then(
      (data) => callback(null, data),
      (err) => callback(err)
    );
  },

  /**
   * saveUserCharacterAddonSettings
   */
  saveUserCharacterAddonSettings(
    params: TCharacterAddonSettingsSaveParams,
    callback: TCallback<TAddonSettingsSaveResponse>
  ) {
    const hostPath = '/api/settings/characters/addon';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'saveUserCharacterAddonSettings request error');
      callback(err);
    });
  },

  /**
   * getHtmlFromBBCode
   */
  convertBBCodeToHtml(params: TConvertBBCodeToHtml, callback: TCallback<any>) {
    const hostPath = '/api/news/convert-bbcode-to-html';
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, { content: params.bbcode } as any)
      .then(callbackResolve(callback))
      .catch((err) => {
        frontendLog.error({ err }, 'convertBBCodeToHtml request error');
        callback(err);
      });
  },

  /**
   * viewEventList
   */
  viewEventsList(params: TViewEventListParams, callback: TCallback<any>) {
    const hostPath = '/api/events';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getPublicEventDetails
   */
  getPublicEventDetails(slug: string, callback: TCallback<any>) {
    const hostPath = `/api/events/${slug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * viewEventUserRegistration
   */
  viewEventUserRegistration(params: TViewEventUserRegistrationParams, callback: TCallback<{}>) {
    const hostPath = '/api/events/registration';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * viewUserEventRegistrationDetails
   */
  viewUserEventRegistrationDetails(params: TViewUserEventRegistrationDetailsParams, callback: TCallback<{}>) {
    const { slug, ...rest } = params;
    const hostPath = `/api/events/${slug}/registration/details`;
    const url = buildUrl(hostPath, rest);
    PERFORM_GET(url, callback);
  },

  /**
   * saveEventUserRegistration
   */
  saveEventUserRegistration(params: TSaveEventUserRegistrationParams, callback: TCallback<{}>) {
    const hostPath = '/api/events/registration';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * cancelEventUserRegistration
   */
  cancelEventUserRegistration(params: TCancelEventUserRegistrationParams, callback: TCallback<{}>) {
    const hostPath = '/api/events/registration/cancel';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * cancelEventEntriesTeamRegistration
   */
  cancelEventEntriesTeamRegistration(params: TCancelEventEntriesTeamRegistrationParams, callback: TCallback<{}>) {
    const hostPath = '/api/events/registration/cancel-entries-team';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * viewEventTeamList
   */
  viewEventTeamList(params: TGetEventTeamList, callback: TCallback<any>) {
    const hostPath = `/api/events/${params.eventSlug}/teams`;
    const url = buildUrl(hostPath, _.pick(params, ['page', 'pageSize', 'search']));
    PERFORM_GET(url, callback);
  },

  /**
   * viewEventReminderList
   */
  viewEventReminderList(params: TGetEventReminderListParams, callback: TCallback<any>) {
    const hostPath = `/api/events/${params.eventSlug}/reminders`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * viewEventTeamDetails
   */
  viewEventTeamDetails(params: TViewEventTeamDetailsParams, callback: TCallback<any>) {
    const hostPath = '/api/teams/event-team-details';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getEventManagementBracketList
   */
  getEventManagementBracketList(params: TgetEventManagementBracketListParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketList
   */
  getEventBracketList(params: TgetEventManagementBracketListParams, callback: TCallback<any>) {
    const hostPath = `/api/events/${params.eventSlug}/brackets`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketDetails
   */
  getEventBracketDetails(params: TGetEventBracketDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events/${params.eventSlug}/brackets/${params.bracketSlug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * Get Game Roster
   */
  getEventBracketGameRoster(params: TGetEventBracketGameRosterParams, callback: TCallback<any>) {
    const hostPath = `/api/events/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}/games/${params.gameId}/roster`;
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getEventManagementBracketDetails
   */
  getEventManagementBracketDetails(params: TGetEventBracketDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * addCollectionWinnerTeam
   */
  addCollectionWinnerTeam(params: TAddCollectionWinnerParams, callback: TCallback<{}>) {
    const { id, payload } = params;
    const hostPath = `/api/events-management/collections/${id}/winners`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, payload, callback);
  },

  /**
   * editCollectionWinner
   */
  editCollectionWinner(params: TEditCollectionWinnerParams, callback: TCallback<{}>) {
    const { id, winnerTeamId, payload } = params;
    const hostPath = `/api/events-management/collections/${id}/winners/${winnerTeamId}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, payload, callback).catch((err) => {
      frontendLog.error({ err }, 'edit collection winner request error');
      callback(err);
    });
  },

  /**
   * removeCollectionWinnerTeam
   */
  removeCollectionWinnerTeam(params: TRemoveCollectionWinnerParams, callback: TCallback<{}>) {
    const { id, winnerTeamId } = params;
    const hostPath = `/api/events-management/collections/${id}/winners/${winnerTeamId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeCollectionWinnerTeam request error');
      callback(err);
    });
  },

  /**
   * Add Event Winner Team
   */
  addEventWinnerTeam(params: TAddEventWinnerParams, callback: TCallback<{}>) {
    const { eventSlug, payload } = params;
    const hostPath = `/api/events-management/${eventSlug}/winners`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, payload, callback);
  },

  /**
   * editCollectionWinner
   */
  editEventWinner(params: TEditEventWinnerParams, callback: TCallback<{}>) {
    const { eventSlug, winnerTeamId, payload } = params;
    const hostPath = `/api/events-management/${eventSlug}/winners/${winnerTeamId}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, payload, callback).catch((err) => {
      frontendLog.error({ err }, 'edit collection winner request error');
      callback(err);
    });
  },

  /**
   * Remove Event Winner Team
   */
  removeEventWinnerTeam(params: TRemoveEventWinnerParams, callback: TCallback<{}>) {
    const { eventSlug, winnerTeamId } = params;
    const hostPath = `/api/events-management/${eventSlug}/winners/${winnerTeamId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeEventWinnerTeam request error');
      callback(err);
    });
  },

  /**
   * addEventBracketPlaceholders
   */

  addEventBracketPlaceholders(params: TAddEventBracketPlaceholders, callback: TCallback<{}>) {
    const { eventSlug, bracketSlug } = params;
    const hostPath = `/api/events-management/${eventSlug}/brackets/${bracketSlug}/batch-placeholders`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * deleteEventBracketPlaceholders
   */

  deleteEventBracketPlaceholders(params: TAddEventBracketPlaceholders, callback: TCallback<{}>) {
    const { eventSlug, bracketSlug } = params;
    const hostPath = `/api/events-management/${eventSlug}/brackets/${bracketSlug}/batch-placeholders`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteEventBracketPlaceholders request error');
      callback(err);
    });
  },

  /**
   * addEventBracketDungeon
   */
  addEventBracketDungeon(params: TAddEventBracketDungeonParams, callback: TCallback<{}>) {
    const { eventSlug, bracketSlug, payload } = params;
    const hostPath = `/api/events-management/${eventSlug}/brackets/${bracketSlug}/event-bracket-dungeons-suggestion`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, payload, callback);
  },

  /**
   * addEventBracketDungeons
   */
  addEventBracketDungeons(params: TAddEventBracketDungeonParams, callback: TCallback<{}>) {
    const { eventSlug, bracketSlug } = params;
    const hostPath = `/api/events-management/${eventSlug}/brackets/${bracketSlug}/event-bracket-dungeons-suggestions`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * removeEventBracketDungeon
   */
  removeEventBracketDungeon(params: TRemoveEventBracketDungeonParams, callback: TCallback<{}>) {
    const { eventSlug, bracketSlug, id } = params;
    const hostPath = `/api/events-management/${eventSlug}/brackets/${bracketSlug}/event-bracket-dungeons-suggestion/${id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeEventBracketDungeon request error');
      callback(err);
    });
  },

  /**
   * editEventBracketDungeon
   */
  editEventBracketDungeon(params: TAddEventBracketDungeonParams, callback: TCallback<{}>) {
    const { eventSlug, bracketSlug, payload } = params;
    const hostPath = `/api/events-management/${eventSlug}/brackets/${bracketSlug}/event-bracket-dungeons-suggestion/${payload.id}`;
    const url = buildUrl(hostPath, {});
    networkService
      .put(url, _.omit(payload, 'id'))
      .then(callbackResolve(callback))
      .catch((err) => {
        frontendLog.error({ err }, 'editEventBracketDungeon request error');
        callback(err);
      });
  },

  /**
   * getEventBracketTeamList
   */
  getEventBracketTeamList(params: TGetEventBracketTeamListParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/teams`;
    const url = buildUrl(hostPath, _.omit(params.filters, 'eventSlug', 'bracketSlug'));
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketMatches
   */
  getEventBracketMatches(params: TGetEventBracketMatchesParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketMatchDetails
   */
  getEventBracketMatchDetails(params: TGetEventBracketMatchDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketMatchGames
   */
  getEventBracketMatchGames(params: TGetEventBracketMatchGamesParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}/games`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketMatchGameDetails
   */
  getEventBracketMatchGameDetails(params: TGetEventBracketMatchGameDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}/games/${params.gameId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketTeamSuggestions
   */
  getEventBracketTeamSuggestions(params: TGetEventBracketTeamSuggestionsParams, callback: TCallback<any>) {
    const { eventSlug, bracketSlug, ...queryParams } = params;
    const hostPath = `/api/events-management/${eventSlug}/brackets/${bracketSlug}/event-team-suggestions`;
    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketTeamSuggestions
   */
  getEventWinnerCollectionSuggestions(params: TGetCollectionWinnerSuggestionsParams, callback: TCallback<any>) {
    const { collectionId, ...queryParams } = params;
    const hostPath = `/api/events-management/collections/${collectionId}/event-team-winners-suggestions`;
    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * getEventBracketTeamSuggestions
   */
  getEventWinnerEventSuggestions(params: TGetEventWinnerSuggestionsParams, callback: TCallback<any>) {
    const { eventSlug, ...queryParams } = params;
    const hostPath = `/api/events-management/${eventSlug}/event-team-winners-suggestions`;
    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * createEventBracket
   */
  createEventBracket(params: TCreateEventBracketParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params.payload, callback);
  },

  /**
   * editEventBracket
   */
  editEventBracket(params: TEditEventBracketParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_PUT(url, params.payload, callback).catch((err) => {
      frontendLog.error({ err }, 'editEventBracket request error');
      callback(err);
    });
  },

  /**
   * removeEventBracket
   */
  removeEventBracket(params: TRemoveEventBracketParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeEventBracket request error');
      callback(err);
    });
  },

  /**
   * copyEventBracket
   */
  copyEventBracket(params: TCopyEventBracketParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/copy`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * addEventSpecRestriction
   */
  addEventSpecRestriction(params: TAddEventSpecRestrictionParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/spec-restrictions`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params.payload, callback);
  },

  /**
   * deleteEventSpecRestriction
   */
  removeEventSpecRestriction(params: TDeleteEventSpecRestrictionParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/spec-restrictions`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, { specIds: params.specIds }, callback).catch((err) => {
      frontendLog.error({ err }, 'removeEventSpecRestriction request error');
      callback(err);
    });
  },

  /**
   * setEventBracketCompRules
   */
  setEventBracketCompRules(params: TSetEventBracketCompRulesParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/comp-rules`;
    const url = buildUrl(hostPath, {});
    PERFORM_PUT(url, { rules: params.rules }, callback).catch((err) => {
      frontendLog.error({ err }, 'setEventBracketCompRules request error');
      callback(err);
    });
  },

  /**
   * addEventSpecRestriction
   */
  addEventReplayRestriction(params: TAddEventReplayRestrictionParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/replay-restrictions`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params.payload, callback);
  },

  /**
   * deleteEventSpecRestriction
   */
  removeEventReplayRestriction(params: TDeleteEventReplayRestrictionParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/replay-restrictions/${params.restrictionId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeEventSpecRestriction request error');
      callback(err);
    });
  },

  /**
   * addEventBracketTeam
   */
  addEventBracketTeam(params: TAddEventBracketTeam, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/teams`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, { teamId: params.team.id }, callback);
  },

  /**
   * editBracketTeam
   */
  editBracketTeam(params: TUpdateBracketTeam, callback: TCallback<any>) {
    const { payload } = params;
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/team/${payload.teamId}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, _.omit(payload, 'teamId'), callback).catch((err) => {
      frontendLog.error({ err }, 'editBracketTeam request error');
      callback(err);
    });
  },

  /**
   * removeEventBracketTeam
   */
  removeEventBracketTeam(params: TRemoveEventBracketTeam, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/team`;
    const url = buildUrl(hostPath, {});
    networkService
      .delete(url, { teamId: params.team.teamId })
      .then(callbackResolve(callback))
      .catch((err) => {
        frontendLog.error({ err }, 'removeEventBracketTeam request error');
        callback(err);
      });
  },

  removeEventBracketTeams(params: TRemoveEventBracketTeams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/teams`;
    const url = buildUrl(hostPath, {});
    networkService
      .delete(url, { mode: params.mode })
      .then(callbackResolve(callback))
      .catch((err) => {
        frontendLog.error({ err }, 'removeEventBracketTeams request error');
        callback(err);
      });
  },

  /**
   * checkInEventBracketTeam
   */
  checkInEventBracketTeam(params: TCheckInEventBracketTeam, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/teams/check-in`;
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, { bracketTeamId: params.bracketTeamId })
      .then(callbackResolve(callback))
      .catch((err) => {
        frontendLog.error({ err }, 'checkInEventBracketTeam request error');
        callback(err);
      });
  },

  /**
   * checkOutEventBracketTeam
   */
  checkOutEventBracketTeam(params: TCheckOutEventBracketTeam, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/teams/check-out`;
    const url = buildUrl(hostPath, {});
    networkService
      .post(url, { bracketTeamId: params.bracketTeamId })
      .then(callbackResolve(callback))
      .catch((err) => {
        frontendLog.error({ err }, 'checkOutEventBracketTeam request error');
        callback(err);
      });
  },

  /**
   * Add eligible team to bracket
   */
  addEligibleTeamToBracket(params: TAddEligibleTeamsToBracket, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/add-eligible-teams`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * createBracketMatches
   */
  createBracketMatches(params: TCreateBracketMatches, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/create-matches`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * deleteBracketMatches
   */
  deleteBracketMatches(params: TDeleteBracketMatches, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/remove-matches`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteBracketMatches request error');
      callback(err);
    });
  },

  /**
   * Reset bracket matches
   */
  resetBracketMatches(params: TResetBracketMatches, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/reset-matches`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * Seed bracket matches
   */

  seedBracketMatches(params: TSeedBracketMatches, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/seed-matches`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, { algorithm: params.algorithm }, callback);
  },

  /**
   * Seed bracket matches
   */

  seedBracketTeamsRandomly(params: TSeedBracketTeamsRandomly, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/seed-teams-randomly`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * batchUpdateBracketMatches
   */
  batchUpdateBracketMatches(params: TBatchUpdateBracketMatches, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/batch-update-matches`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params.payload, callback);
  },

  /**
   * batchUpdateBracketTeams
   */
  batchUpdateBracketTeams(params: TBatchUpdateBracketTeams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/batch-update-teams`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params.payload, callback);
  },

  /**
   * editEventBracketMatchDetails
   */
  editEventBracketMatchDetails(params: TEditEventBracketMatchDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_PUT(url, params.payload, callback).catch((err) => {
      frontendLog.error({ err }, 'editEventBracket request error');
      callback(err);
    });
  },

  /**
   * addEventBracketMatchAdditionalGame
   */
  addEventBracketMatchAdditionalGame(params: TEditEventBracketMatchDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}/bonus-round`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * removeEventBracketMatchAdditionalGame
   */
  removeEventBracketMatchAdditionalGame(params: TEditEventBracketMatchDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}/bonus-round`;
    const url = buildUrl(hostPath, {});
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeAdditionalGame request error');
      callback(err);
    });
  },

  /**
   * editEventBracketMatchGameDetails
   */
  editEventBracketMatchGameDetails(params: TEditEventBracketMatchGameDetailsParams, callback: TCallback<any>) {
    const hostPath = `/api/events-management/${params.eventSlug}/brackets/${params.bracketSlug}/matches/${params.matchId}/games/${params.gameId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_PUT(url, params.payload, callback).catch((err) => {
      frontendLog.error({ err }, 'editEventBracket request error');
      callback(err);
    });
  },

  /**
   * getEventDetails
   */
  getEventDetails(params: TEventDetailsParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${params.eventSlug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getEventRegistrationStaticData
   */
  getEventRegistrationStaticData(callback: TCallback<{}>) {
    const hostPath = '/api/events-management/registration-static-data';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * createEvent
   */
  createEvent(params: TEventBodyPayload, callback: TCallback<{}>) {
    const hostPath = '/api/events-management';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * Create Legacy Event
   */
  createLegacyEvent(params: TLegacyEventParams, callback: TCallback<{}>) {
    const hostPath = '/api/events-management/legacy';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  },

  /**
   * editEvent
   */
  editEvent(slug: string, params: TEventBodyPayload, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${slug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_PUT(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'editEvent request error');
      callback(err);
    });
  },

  /**
   * Edit Legacy event
   */
  editLegacyEvent(slug: string, params: TEventBodyPayload, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${slug}/legacy`;
    const url = buildUrl(hostPath, {});
    PERFORM_PUT(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'editEvent request error');
      callback(err);
    });
  },

  /**
   * Lock Event
   */
  lockEvent(slug: string, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${slug}/lock`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * removeEvent
   */
  removeEvent(slug: string, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${slug}/remove`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * copyEvent
   */
  copyEvent(slug: string, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${slug}/copy`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * getEventCollaboratorSuggestions
   */
  getEventCollaboratorSuggestions(
    params: TGetEventCollaboratorSuggestionsParams,
    callback: TCallback<IEventCollaboratorSummary[]>
  ) {
    const { slug, ...queryParams } = params;
    const hostPath = `/api/events-management/${slug}/collaborator-suggestions`;
    const url = buildUrl(hostPath, queryParams);
    PERFORM_GET(url, callback);
  },

  /**
   * addEventCollaborators
   */
  addEventCollaborator(params: TAddEventCollaborator, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${params.slug}/add-collaborator/${params.collaborator.id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * removeEventCollaborator
   */
  removeEventCollaborator(params: TRemoveEventCollaborator, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${params.slug}/remove-collaborator/${params.collaborator.id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  /**
   * getEventCollectionSuggestions
   */
  getEventCollectionSuggestions(params: TGetEventCollectionSuggestionsParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/collection-suggestions/${params.collectionId}`;
    const url = buildUrl(hostPath, params.query || {});
    PERFORM_GET(url, callback);
  },

  /**
   * addEventToCollection
   */
  addEventToCollection(params: TAddEventToEventCollectionParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${params.eventSlug}/collections/${params.collectionId}`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback);
  },

  removeEventFromCollection(params: TRemoveEventFromEventCollectionParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/${params.eventSlug}/collections/${params.collectionId}`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeEventFromCollection request error');
      callback(err);
    });
  },

  createEventCollection(params: TCreateEventCollectionParams, callback: TCallback<{}>) {
    const hostPath = '/api/events-management/collections';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'createEventCollection request error');
      callback(err);
    });
  },

  editEventCollection(params: TEditEventCollectionParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/collections/${params.id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, params.payload, callback).catch((err) => {
      frontendLog.error({ err }, 'createEventCollection request error');
      callback(err);
    });
  },

  deleteEventCollection(params: TDeleteEventCollectionParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/collections/${params.id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteEventCollection request error');
      callback(err);
    });
  },

  getEventCollections(params: TGetEventCollectionListParams = {}, callback: TCallback<{}>) {
    const hostPath = '/api/events-management/collections';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getEventCollection(params: TGetEventCollectionDetailsParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/collections/${params.id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /**
   * addEventVideo
   */

  addEventVideo(params: TAddEventVideoParams, callback: TCallback<{}>) {
    const hostPath = '/api/events-management/videos';
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'addEventVideo request error');
      callback(err);
    });
  },

  editEventVideo(id: number, params: TEditEventVideoParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/videos/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'editEventVideo request error');
      callback(err);
    });
  },

  removeEventVideo(params: TRemoveEventVideoParams, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/videos/${params.id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'removeEventVideo request error');
      callback(err);
    });
  },

  getEventVideos(params: TGetEventVideosParams, callback: TCallback<{}>) {
    const hostPath = '/api/events-management/videos';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  updateEventVideos(params: TUpdateEventVideosParams, callback: TCallback<{}>) {
    const hostPath = '/api/events-management/videos';
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'updateEventVideos request error');
      callback(err);
    });
  },

  unlinkFinalEventVideo(params: TUnlinkFinalEventVideo, callback: TCallback<{}>) {
    const hostPath = `/api/events-management/videos/${params.id}/unlink-final`;
    const url = buildUrl(hostPath, {});

    PERFORM_POST(url, { eventSlug: params.eventSlug }, callback).catch((err) => {
      frontendLog.error({ err }, 'unlink Final Event Video request error');
      callback(err);
    });
  },

  /**
   * debugSelfReportScores
   */
  debugSelfReportScores(params: any, callback: TCallback<any>) {
    const hostPath = '/api/events-management/brackets/matches/debug-self-report';
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'editEventBracket request error');
      callback(err);
    });
  },

  saveSelfReportScores(params: TSaveSelfReportScoresParams, callback: TCallback<any>) {
    const { eventSlug, bracketSlug, payload } = params;
    const hostPath = `/api/events/${eventSlug}/brackets/${bracketSlug}/match-scores`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, payload, callback);
  },

  /**
   * getEventList
   */
  getEventList(params: TGetEventListDataParams, callback: TCallback<{}>) {
    const hostPath = '/api/events-management';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getStorageFile(params: any, callback: TCallback<any>) {
    const hostPath = `/api/storage/${params.id}`;
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getStorageFileList(params: any, callback: TCallback<any>) {
    const hostPath = '/api/storage';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  getStorageFileTags(params: any, callback: TCallback<TStorageTagSummary[]>) {
    const hostPath = '/api/storage/tags';
    const url = buildUrl(hostPath, params);

    PERFORM_GET(url, callback);
  },

  updateStorageTag(id: number, body: TUpdateStorageTagBody, callback: TCallback<any>) {
    const hostPath = `/api/storage/tags/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, body, callback).catch((err) => {
      frontendLog.error({ err }, 'updateStorageTag request error');
      callback(err);
    });
  },

  deleteStorageTag(id: number, callback: TCallback<any>) {
    const hostPath = `/api/storage/tags/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteStorageTag request error');
      callback(err);
    });
  },

  updateStorageFile(params: TUpdateStorageItem, callback: TCallback<any>) {
    const hostPath = `/api/storage/${params.id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_PUT(url, _.omit(params, 'id'), callback).catch((err) => {
      frontendLog.error({ err }, 'updateStorageFile request error');
      callback(err);
    });
  },

  deleteStorageFile(params: any, callback: TCallback<any>) {
    const hostPath = `/api/storage/${params.id}`;
    const url = buildUrl(hostPath, params);
    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteStorageFile request error');
      callback(err);
    });
  },

  /**
   * getTeamDetails
   */
  getTeamDetails(params: TGetTeamDetailsParams, callback: TCallback<any>) {
    const hostPath = '/api/teams/details';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getSidebarContent
   */
  getSidebarContent(slug: string, callback: TCallback<any>) {
    const hostPath = `/api/sidebar/${slug}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getHubCollections
   */
  getHubCollections(params: TGetHubCollectionParams, callback: TCallback<any>) {
    const hostPath = `/api/events/hub-collections/${params.expansionSlug}`;
    const url = buildUrl(hostPath, _.omit(params, 'expansionSlug'));
    PERFORM_GET(url, callback);
  },

  getUpcomingEvents(params: TGetUpcomingEventsParams, callback: TCallback<any>) {
    const hostPath = '/api/events/upcoming';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getActiveEvents(params: TGetActiveEventsParams, callback: TCallback<any>) {
    const hostPath = '/api/events/active';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getLatestEvent(params: TGetLatestEventsParams, callback: TCallback<any>) {
    const hostPath = '/api/events/latest';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  getEventsHistory(params: TGetEventHistoryParams, callback: TCallback<any>) {
    const hostPath = '/api/events/event-history';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * get live event
   */
  getLiveEvent(params: TGetLiveEventParams, callback: TCallback<any>) {
    let hostPath = '/api/events/live';

    if (params.mode) {
      hostPath += `/${params.mode}`;
    }

    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getExpansionCollections
   */
  getExpansionCollections(callback: TCallback<any>) {
    const hostPath = '/api/events/expansion-collections';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * listThreadPosts
   */
  listThreadPosts(params: any, callback: TCallback<any>) {
    const hostPath = '/api/threads/list';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * peekThreadPosts
   */
  peekThreadPosts(params: any, callback: TCallback<any>) {
    const hostPath = '/api/threads/peek';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * refreshThreadPosts
   */
  refreshThreadPosts(params: any, callback: TCallback<any>) {
    const hostPath = '/api/threads/refresh';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * getPostById
   */
  getPostById(id: number, callback: TCallback<any>) {
    const hostPath = `/api/threads/posts/${id}`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * listThreadPosts
   */
  createThreadPost(params: TCreateThreadPost, callback: TCallback<any>) {
    const hostPath = `/api/threads/${params.threadId}/posts`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, { content: params.content }, callback).catch((err) => {
      frontendLog.error({ err }, 'createThreadPost request error');
      callback(err);
    });
  },

  /**
   * listThreadPosts
   */
  closeThread(threadId: number, callback: TCallback<any>) {
    const hostPath = `/api/threads/${threadId}/close`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'closeThread request error');
      callback(err);
    });
  },

  /**
   * blockCommunication
   */
  blockCommunication(threadId: number, reason: string, callback: TCallback<any>) {
    const hostPath = `/api/threads/${threadId}/block`;
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, { reason }, callback).catch((err) => {
      frontendLog.error({ err }, 'blockCommunication request error');
      callback(err);
    });
  },

  /**
   * getNewsListContent
   */
  getNewsListContent(params: TGetNewsParams, callback: TCallback<any>) {
    const hostPath = '/api/news';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  listMyMessage(callback: TCallback<any>) {
    const hostPath = '/api/messages/list';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback).catch((err) => {
      frontendLog.error({ err }, 'listMyMessage request error');
      callback(err);
    });
  },

  fetchUnreadAmount(callback: TCallback<any>) {
    const hostPath = '/api/messages/unread';
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneTeamRankings
   */
  getKeystoneTeamRankings(params: TGetKeystoneTeamRankingsParams, callback: TCallback<any>) {
    const hostPath = '/api/mythic-plus/rankings/teams';
    const url = buildUrl(
      hostPath,
      _.extend(
        {
          region: 'all'
        },
        params
      )
    );

    PERFORM_GET(url, callback);
  },

  /**
   * getKeystoneRunRankings
   */
  getKeystoneRunRankings(params: TGetKeystoneRunRankingsParams, callback: TCallback<any>) {
    const hostPath = '/api/mythic-plus/rankings/runs';
    const url = buildUrl(
      hostPath,
      _.extend(
        {
          region: 'all'
        },
        params
      )
    );
    PERFORM_GET(url, callback);
  },

  // reactions
  adminDeleteReaction(id: number, callback: TCallback<void>) {
    const hostPath = `/admin/api/reactions/${id}`;
    const url = buildUrl(hostPath, {});

    PERFORM_DELETE(url, {}, callback).catch((err) => {
      frontendLog.error({ err }, 'deleteReaction request error');
      callback(err);
    });
  },

  getReactions(params: { entityType: ReactionEntityType; entityId: number }, callback: TCallback<IReactionSummary[]>) {
    const { entityType, entityId } = params;
    const hostPath = `/api/reactions/${entityType}/${entityId}`;
    const url = buildUrl(hostPath, {});

    PERFORM_GET(url, callback);
  },

  /**
   * searchAdvanced
   */
  searchAdvanced(params: TSearchAdvancedParams, callback: TCallback<any>) {
    const hostPath = '/api/search-advanced';
    const url = buildUrl(hostPath, params);
    PERFORM_GET(url, callback);
  },

  /**
   * saveUserTimeSettings
   */
  saveUserTimeSettings(params: any, callback: TCallback<any>) {
    const hostPath = '/api/settings/time';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback).catch((err) => {
      frontendLog.error({ err }, 'saveUserTimeSettings request error');
      callback(err);
    });
  },

  /**
   * getCharacterLoadouts
   */
  getCharacterLoadouts(params: TGetCharacterLoadoutsParams, callback: TCallback<TCharacterLoadoutOption[]>) {
    const hostPath = `/api/characters/${params.region}/${params.realm}/${params.character}/loadouts`;
    const url = buildUrl(hostPath, {});
    PERFORM_GET(url, callback);
  },

  submitModerationReport(params: any, callback: TCallback<any>) {
    const hostPath = '/api/moderation/reports';
    const url = buildUrl(hostPath, {});
    PERFORM_POST(url, params, callback);
  }
};

const promisify =
  (fn: any) =>
  (...args: any[]) =>
    new Promise((resolve: any, reject: any) =>
      fn(...args, (err: any, results: any) => {
        if (err) {
          reject(err);
        } else {
          resolve(results);
        }
      })
    );

type TApiServiceKeys = keyof typeof commonApiService;

type TApiServiceAsync = {
  [key in TApiServiceKeys]: (...args: any) => Promise<any>;
};

export const makeApiServiceAsync = (apiServiceCb: any) =>
  Object.keys(apiServiceCb).reduce<Record<string, any>>((accum, key) => {
    const originalFn = apiServiceCb[key];
    const promisifiedFn = promisify(originalFn);
    Object.defineProperty(promisifiedFn, 'length', { value: originalFn.length - 1 });
    accum[key] = promisifiedFn;
    return accum;
  }, {});

export const commonApiServiceAsync = makeApiServiceAsync(commonApiService) as TApiServiceAsync;

export const makeRetryableApiServiceAsync = (apiServiceAsync: TApiServiceAsync) => {
  const RETRY_DEFAULT_OPTIONS: RetryOptions = {
    retries: 5,
    initialDelay: 1000,
    delayFunction: (attempt, initialDelay) => initialDelay * Math.pow(2, attempt - 1), // exponential backoff
    shouldRetry: async (error: any) => {
      const statusCode = error?.statusCode || error?.status || error?.response?.statusCode || error?.response?.status;

      if (statusCode >= 500) {
        return true;
      }

      return false;
    }
  };

  const retryableApiServiceAsync = Object.keys(apiServiceAsync).reduce<Record<string, any>>((accum, key) => {
    const originalFn = apiServiceAsync[key as keyof typeof apiServiceAsync];

    const fnWithRetry = async (...args: any[]) => {
      let config: Partial<RetryOptions> = {};

      const expectedParamCount = originalFn.length;
      const hasConfig = args.length > expectedParamCount;

      if (hasConfig && typeof args[args.length - 1] === 'object') {
        config = args.pop()!;
      }

      const mergedConfig = Object.assign({}, RETRY_DEFAULT_OPTIONS, config);
      return retry(() => originalFn(...args), mergedConfig);
    };

    accum[key] = fnWithRetry;

    return accum;
  }, {});

  return retryableApiServiceAsync;
};

export const retryableCommonApiServiceAsync = makeRetryableApiServiceAsync(commonApiServiceAsync) as TApiServiceAsync;
